/**
 * Well-known token metadata used across the platform.
 *
 * These are canonical definitions for tokens referenced in multiple packages.
 * Import these instead of hardcoding addresses, decimals, names, etc.
 */

import type { ChainAsset } from './groups';

export type WellKnownToken = Pick<
  ChainAsset & { assetId: NonNullable<ChainAsset['assetId']> },
  'assetId' | 'symbol' | 'chain' | 'address' | 'decimals' | 'name' | 'imgUrl'
>;

// ─────────────────────────────────────────────────────────────────────────────
// USDC
// ─────────────────────────────────────────────────────────────────────────────

export const USDC_ARBITRUM = {
  assetId: 'usd_coin',
  symbol: 'USDC',
  chain: 'arbitrum',
  address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
  decimals: 6,
  name: 'USD Coin',
  imgUrl: 'https://res.cloudinary.com/infinex/image/upload/tokens/usd_coin.svg',
} as const satisfies WellKnownToken;

export const USDC_HYPER_EVM = {
  assetId: 'usd_coin',
  symbol: 'USDC',
  chain: 'hyperEVM',
  address: '0xb88339CB7199b77E23DB6E890353E22632Ba630f',
  decimals: 6,
  name: 'USD Coin',
  imgUrl: 'https://res.cloudinary.com/infinex/image/upload/tokens/usd_coin.svg',
} as const satisfies WellKnownToken;

export const USDC_BASE = {
  assetId: 'usd_coin',
  symbol: 'USDC',
  chain: 'base',
  address: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
  decimals: 6,
  name: 'USD Coin',
  imgUrl: 'https://res.cloudinary.com/infinex/image/upload/tokens/usd_coin.svg',
} as const satisfies WellKnownToken;

export const USDC_ETHEREUM = {
  assetId: 'usd_coin',
  symbol: 'USDC',
  chain: 'ethereum',
  address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
  decimals: 6,
  name: 'USD Coin',
  imgUrl: 'https://res.cloudinary.com/infinex/image/upload/tokens/usd_coin.svg',
} as const satisfies WellKnownToken;

export const USDC_OPTIMISM = {
  assetId: 'usd_coin',
  symbol: 'USDC',
  chain: 'optimism',
  address: '0x0b2c639c533813f4aa9d7837caf62653d097ff85',
  decimals: 6,
  name: 'USD Coin',
  imgUrl: 'https://res.cloudinary.com/infinex/image/upload/tokens/usd_coin.svg',
} as const satisfies WellKnownToken;

export const USDC_POLYGON = {
  assetId: 'usd_coin',
  symbol: 'USDC',
  chain: 'polygon',
  address: '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359',
  decimals: 6,
  name: 'USD Coin',
  imgUrl: 'https://res.cloudinary.com/infinex/image/upload/tokens/usd_coin.svg',
} as const satisfies WellKnownToken;

export const USDC_SOLANA = {
  assetId: 'usd_coin',
  symbol: 'USDC',
  chain: 'solana',
  address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
  decimals: 6,
  name: 'USD Coin',
  imgUrl: 'https://res.cloudinary.com/infinex/image/upload/tokens/usd_coin.svg',
} as const satisfies WellKnownToken;

// Legacy address-only exports (for backward compatibility)
export const USDC_BASE_ADDRESS = USDC_BASE.address;
export const USDC_ARBITRUM_ADDRESS = USDC_ARBITRUM.address;
export const USDC_ETHEREUM_ADDRESS = USDC_ETHEREUM.address;
export const USDC_OPTIMISM_ADDRESS = USDC_OPTIMISM.address;
export const USDC_POLYGON_ADDRESS = USDC_POLYGON.address;
export const USDC_SOLANA_ADDRESS = USDC_SOLANA.address;

// ─────────────────────────────────────────────────────────────────────────────
// USDe (Ethena)
// ─────────────────────────────────────────────────────────────────────────────

export const USDE_HYPER_EVM = {
  assetId: 'usd_ethena',
  symbol: 'USDe',
  chain: 'hyperEVM',
  address: '0x5d3a1Ff2b6BAb83b63cd9AD0787074081a52ef34',
  decimals: 18,
  name: 'Ethena USDe',
  imgUrl:
    'https://res.cloudinary.com/infinex/image/upload/tokens/usd_ethena.svg',
} as const satisfies WellKnownToken;

// ─────────────────────────────────────────────────────────────────────────────
// USDH
// ─────────────────────────────────────────────────────────────────────────────

export const USDH_HYPER_EVM = {
  assetId: 'usdh',
  symbol: 'USDH',
  chain: 'hyperEVM',
  address: '0x111111a1a0667d36bD57c0A9f569b98057111111',
  decimals: 6,
  name: 'USDH',
  imgUrl: 'https://res.cloudinary.com/infinex/image/upload/tokens/usdh.png',
} as const satisfies WellKnownToken;

// ─────────────────────────────────────────────────────────────────────────────
// USDT (Tether)
// ─────────────────────────────────────────────────────────────────────────────

export const USDT_ETHEREUM = {
  assetId: 'usd_tether',
  symbol: 'USDT',
  chain: 'ethereum',
  address: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  decimals: 6,
  name: 'Tether USD',
  imgUrl: 'https://res.cloudinary.com/infinex/image/upload/tokens/tether.svg',
} as const satisfies WellKnownToken;
